import { CalendarCheck, Clock, FileText, MapPin, MessageCircle, Phone, Send } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero, SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { CTABanner } from "@/components/CTABanner";
import { PineappleIcon } from "@/components/PineappleIcon";
import { BUSINESS_HOURS, TELEGRAM_URL, PHONE_DISPLAY, waLink } from "@/lib/site";

const STEPS = [
  { icon: MessageCircle, title: "WhatsApp us", desc: "Send your key collection date, flat type and address." },
  { icon: FileText, title: "Share your floor plan", desc: "We confirm your exact quote and the right scope." },
  { icon: CalendarCheck, title: "We inspect", desc: "Your slot is locked in — usually within a few hours of booking." },
];

const DETAILS = [
  { icon: Phone, label: "WhatsApp / Call", value: PHONE_DISPLAY },
  { icon: Clock, label: "Hours", value: BUSINESS_HOURS },
  { icon: MapPin, label: "Coverage", value: "Islandwide, Singapore" },
];

export default function Contact() {
  return (
    <>
      <SEO
        title="Contact & Book | WhatsApp +65 8490 0316 — Lobang Home Inspection"
        description="Book your BTO, condo or landed defect inspection on WhatsApp at +65 8490 0316. Fast replies, islandwide coverage across Singapore."
      />
      <PageHero
        eyebrow="Contact"
        title="Your inspection is one message away"
        copy="No forms, no waiting on email — WhatsApp is the fastest way to reach us and lock in your slot."
      />

      <section className="px-5 py-16 sm:px-8 sm:py-24" data-testid="contact-main">
        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <div className="relative h-full overflow-hidden rounded-[2rem] bg-charcoal p-8 sm:p-12" data-testid="contact-whatsapp-card">
              <PineappleIcon className="pointer-events-none absolute -bottom-8 -right-6 h-48 w-32 rotate-12 text-gold/10" strokeWidth={1} />
              <p className="eyebrow">Fastest Response</p>
              <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-ivory sm:text-4xl">
                Book on WhatsApp
              </h2>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-ivory/70 sm:text-base">
                Tap below and a pre-filled message opens in your WhatsApp. Add your key collection date and flat type —
                we'll take it from there.
              </p>
              <p className="mt-6 font-serif text-2xl font-semibold text-gold-light" data-testid="contact-phone-display">
                {PHONE_DISPLAY}
              </p>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-cta"
                className="relative z-10 mt-7 inline-flex items-center gap-2 rounded-full bg-wa px-8 py-4 text-sm font-bold text-white shadow-wa transition-transform duration-300 hover:scale-[1.04]"
              >
                <Send className="h-5 w-5" />
                Open WhatsApp Chat
              </a>
              <div className="relative z-10 mt-8 flex flex-col items-start gap-4 border-t border-white/20 pt-6 sm:flex-row sm:items-center">
                <a href={waLink()} target="_blank" rel="noopener noreferrer" aria-label="Open WhatsApp or scan this QR code" className="shrink-0 rounded-2xl bg-white p-3">
                  <img src="/assets/whatsapp-qr.png" alt="Scan to contact Lobang Home Inspection on WhatsApp" width="180" height="176" className="h-auto w-44" />
                </a>
                <div><p className="font-semibold text-ivory">Scan to chat on WhatsApp</p><p className="mt-2 text-sm leading-relaxed text-ivory/80">On your phone? Tap the QR code or the button above.</p></div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="flex h-full flex-col justify-between gap-6 rounded-[2rem] border border-border bg-white p-8 shadow-luxury sm:p-10" data-testid="contact-details-card">
              <div className="space-y-6">
                <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-telegram" className="flex items-start gap-4 rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-cream text-gold-dark"><Send className="h-5 w-5" /></span>
                  <div className="min-w-0"><p className="text-xs font-bold uppercase tracking-widest text-charcoal-mute">Telegram</p><p className="mt-1 break-words text-base font-semibold text-charcoal">@lobanghomeinspection</p></div>
                </a>
                {DETAILS.map((d) => (
                  <div key={d.label} className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-cream text-gold-dark">
                      <d.icon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest text-charcoal-mute">{d.label}</p>
                      <p className="mt-1 font-serif text-lg font-semibold text-charcoal">{d.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="rounded-2xl bg-cream p-4 text-xs leading-relaxed text-charcoal-soft">
                Inspecting new BTO flats, condominiums and landed homes across Singapore — from Punggol to Pasir Ris,
                Tengah to Tampines.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream px-5 py-20 sm:px-8 sm:py-24" data-testid="contact-steps">
        <div className="mx-auto max-w-6xl">
          <SectionHeading eyebrow="How Booking Works" title="Three taps to a booked inspection" />
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-border bg-white p-7 text-center shadow-luxury">
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-cream text-gold-dark">
                    <s.icon className="h-6 w-6" />
                  </span>
                  <p className="mt-4 font-serif text-sm italic text-gold-dark">Step {i + 1}</p>
                  <h3 className="mt-1 font-serif text-xl font-semibold text-charcoal">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-charcoal-soft">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
